import "./GamifiedQuiz.css";
import GamifiedQuizTaskLogo from "../../../../assets/images/GamifiedQuizTaskLogo.svg";
import { useContext, useEffect, useState } from "react";
import QuestContext from "../../../../components/QuestWrapper";
import General from "../../../../general";
import ThankYouPopup from "./ThanksPopup";
import { useThemeManager } from "./useThemeManager";
import {
  GamifiedQuizOfflineProps,
  StyleConfig,
} from "../types/GamifiedQuiz.type";
import GamifiedQuizFooter from "./GamifiedQuizFooter";
import { GamifiedQuizQuestions } from "./GamifiedQuizQuestion";
import { NavigateButton } from "./NavigateButton";
import { useAnswerManagement } from "./useAnswerManagement";
import { TaskLogo } from "./SVG";
import { hexToGlassColor } from "../../helphub/component/chatcomponents/helperfunction";

interface ValidationState {
  [key: string]: {
    isValid: boolean;
    regexValid: boolean;
  };
}

const defaultEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateField = (
  questionId: string,
  value: any,
  question: any,
  selectedOptions: Record<string, Array<string>>,
  answer: Record<string, string | Array<string>>
) => {
  let isValid = true;
  let regexValid = true;

  if (question.required) {
    switch (question.type) {
      case "USER_INPUT_MULTI_CHOICE":
        isValid = selectedOptions[questionId]?.length > 0;
        break;
      case "USER_INPUT_SINGLE_CHOICE":
        isValid = !!answer[questionId];
        break;
      case "RATING":
        isValid = answer[questionId] !== undefined && answer[questionId] !== "";
        break;
      case "USER_INPUT_TEXT":
      case "USER_INPUT_EMAIL":
      case "USER_INPUT_TEXTAREA":
      case "USER_INPUT_DATE":
      case "USER_INPUT_PHONE":
      case "USER_INPUT_NUMBER":
        isValid = value !== undefined && value !== "";
        break;
      default:
        isValid = !!value;
    }
  }

  if (question.type === "USER_INPUT_EMAIL" && value) {
    if (question.regexKey) {
      try {
        const regex = new RegExp(question.regexKey);
        regexValid = regex.test(value.toString());
      } catch (e) {
        regexValid = defaultEmailPattern.test(value.toString());
      }
    } else {
      regexValid = defaultEmailPattern.test(value.toString());
    }
  } else if (question.regexKey && value) {
    try {
      const regex = new RegExp(question.regexKey);
      regexValid = regex.test(value.toString());
    } catch (e) {
      regexValid = true;
    }
  }

  return { isValid, regexValid };
};

const GamifiedQuizOffline: React.FC<GamifiedQuizOfflineProps> = ({
  heading,
  styleConfig,
  showFooter = true,
  thanksPopUpFooter = true,
  feedbackContent,
  footerText,
  footerLink,
  design = [],
  setGamifiedQuiz,
  gamifiedQuiz,
  questionsPerSection = 0,
  functionOnSubmit,
  formDataOffline,
  setOfflineAnswer,
  Headers,
  singleChoiceModal,
  multiChoiceModal,
  ratingType,
  isV1Api,
  translatedData,
  translatedButton,
  BrandTheme,
  QuestThemeData,
  isGradientBackground,
  gradientBackgroundColor,
}) => {
  const { apiType, themeConfig } = useContext(QuestContext.Context);

  const [formdata, setFormdata] = useState<FormData[] | []>([]);
  const [totalSectionsPerSectionQuestion, setTotalSectionsPerSectionQuestion] =
    useState<number>(0);
  const [currentSection, setCurrentSection] = useState<number>(0);

  const [sectionNo, setSectionNo] = useState<number>(0);
  let GeneralFunctions = new General("mixpanel", apiType);

  const themeManager = useThemeManager(
    BrandTheme,
    styleConfig as StyleConfig,
    themeConfig,
    QuestThemeData
  );
  useEffect(() => {
    GeneralFunctions.fireTrackingEvent(
      "quest_gamifiedquiz_offline_loaded",
      "gamifiedquiz_offline"
    );

    if (questionsPerSection > 0) {
      setTotalSectionsPerSectionQuestion(
        Math.ceil(formDataOffline.length / questionsPerSection)
      );
    }

    setFormdata(formDataOffline);
  }, [formDataOffline]);

  const newId = isV1Api ? "criteriaId" : "actionId";

  const [thanksPopup, setThanksPopup] = useState(false);
  const {
    selectedOptions,
    answer,
    goToNextSection,
    handleCheckboxChange,
    handleRadioChange,
    handleRatingChange,
    handleUpdate,
    setGoToNextSection,
  } = useAnswerManagement(
    design,
    sectionNo,
    formdata,
    questionsPerSection,
    currentSection,
    newId
  );

  const formSubmitHandler = () => {
    GeneralFunctions.fireTrackingEvent(
      "quest_gamifiedquiz_offline_submit_button_clicked",
      "gamifiedquiz_offline"
    );
    for (const key in selectedOptions) {
      if (selectedOptions.hasOwnProperty(key)) {
        answer[key] = selectedOptions[key];
      }
    }
    functionOnSubmit();
    setOfflineAnswer(answer);
    setThanksPopup(true);
  };
  const [validationState, setValidationState] = useState<ValidationState>({});

  const updateValidationState = () => {
    if (design[sectionNo]) {
      const newValidationState: ValidationState = {};

      design[sectionNo].forEach((questionIndex) => {
        const question = formDataOffline[questionIndex - 1];
        if (question) {
          const value = answer[question[newId]];
          const validation = validateField(
            question[newId],
            value,
            question,
            selectedOptions,
            answer
          );
          newValidationState[question[newId]] = validation;
        }
      });

      setValidationState(newValidationState);
      const isSectionValid = Object.values(newValidationState).every(
        (state) => state.isValid && state.regexValid
      );
      setGoToNextSection(isSectionValid);
    }
  };

  const validateInputRegex = (validated: boolean, actionId: string) => {
    if (!validationState[actionId]) return;

    setValidationState((prev) => ({
      ...prev,
      [actionId]: {
        ...prev[actionId],
        regexValid: validated,
      },
    }));

    const isAllValid = Object.values({
      ...validationState,
      [actionId]: {
        ...validationState[actionId],
        regexValid: validated,
      },
    }).every((state) => state.isValid && state.regexValid);

    setGoToNextSection(isAllValid);
  };

  useEffect(() => {
    updateValidationState();
  }, [answer, selectedOptions, sectionNo]);

  if(formDataOffline.length == 0) return 


  return (
    <>
      {thanksPopup ? (
        <ThankYouPopup
          setThanksPopup={setThanksPopup}
          setGamifiedQuiz={setGamifiedQuiz}
          feedbackContent={feedbackContent}
          thanksPopUpFooter={thanksPopUpFooter}
          footerText={footerText}
          GeneralFunctions={GeneralFunctions}
          popUpHead={feedbackContent?.FeedbackHeading}
          popUpMessage={feedbackContent?.FeedbackDescription}
          themeManager={themeManager}
        />
      ) : null}

      {gamifiedQuiz && !thanksPopup ? (
        <div className="upper-div">
          <div className="gamified-quiz"
              style={themeManager.getFormStyles()}
          
          >
            <div
              className="gamified-quiz-header-section"
              style={{background: (isGradientBackground && gradientBackgroundColor) ? gradientBackgroundColor : themeManager.getContBgStyle()}}
            >
              
              <div className="gamified-quiz-title-section">
                <div
                  className="gamified-quiz-title-head"
                  style={themeManager.getHeaderStyles().heading}
                >
                  {heading ? heading : "Choose an option"}
                </div>
              </div>

              <div className="div">
                <div className="gamified-quiz-header-task-logo-cont">
                  <TaskLogo primary={themeManager.getTaskLogoColors().primary} secondary={themeManager.getTaskLogoColors().secondary} tertiary={themeManager.getTaskLogoColors().tertiary} />
                </div>

                <div
                  className="gamified-quiz-header-form"
                  style={themeManager.getHeaderFormStyles()}
                >
                  <div className="gamified-quiz-header-form-container">
                    <div className="heading-subheading-container">
                      <div
                        className="quest-title"
                        style={themeManager.getSubHeadingStyles()}
                      >
                        {Headers &&
                        Headers.length > 0 &&
                        Headers[sectionNo]?.heading
                          ? Headers[sectionNo]?.heading
                          : "Fill out the Details"}
                      </div>
                      {Headers &&
                        Headers?.length > 0 &&
                        Headers[sectionNo]?.subHeading && (
                          <div
                            className="question"
                            style={themeManager.getQuestionStyles()}
                          >
                            {Headers[sectionNo].subHeading ||
                              "Fill out the Details"}
                          </div>
                        )}
                    </div>

                    {design?.length > 0 &&
                      design[sectionNo].map((question, index) => (
                        <GamifiedQuizQuestions
                          key={index}
                          question={question}
                          formdata={formDataOffline}
                          answer={answer}
                          handleUpdate={handleUpdate}
                          styleConfig={styleConfig}
                          themeConfig={themeConfig}
                          questThemeData={QuestThemeData}
                          handleCheckboxChange={handleCheckboxChange}
                          handleRadioChange={handleRadioChange}
                          handleRatingChange={handleRatingChange}
                          selectedOptions={selectedOptions}
                          newId={newId}
                          themeManager={themeManager}
                          singleChoiceModal={singleChoiceModal}
                          multiChoiceModal={multiChoiceModal}
                          ratingType={ratingType}
                          validateInputRegex={validateInputRegex}
                        />
                      ))}
                  </div>

                  <NavigateButton
                    design={design}
                    sectionNo={sectionNo}
                    setSectionNo={setSectionNo}
                    GeneralFunctions={GeneralFunctions}
                    setGamifiedQuiz={setGamifiedQuiz}
                    formSubmitHandler={formSubmitHandler}
                    goToNextSection={goToNextSection}
                    isV1Api={isV1Api}
                    translatedData={translatedData}
                    translatedButton={translatedButton}
                    currentSection={currentSection}
                    setCurrentSection={setCurrentSection}
                    totalSectionsPerSectionQuestion={
                      totalSectionsPerSectionQuestion
                    }
                    themeManager={themeManager}
                    formData={formDataOffline}
                  />
                </div>
              </div>
            </div>

            {showFooter && (
              <GamifiedQuizFooter
                footerText={footerText}
                footerLink={footerLink}
                themeManager={themeManager}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GamifiedQuizOffline;
